import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { getStation, getColocatedStations, getTimeSeriesData, prefetchTimeSeriesData } from '../../services/api.service';
import SimpleTimeSeriesChart from './SimpleTimeSeriesChart';

// Variable configuration with complete list from the API
const VARIABLE_CONFIG = {
	// Core weather variables
	te: {
		name: 'Temperature',
		unit: '°C',
		color: '#fd7e14',
		category: 'sensor',
	},
	pr: {
		name: 'Precipitation',
		unit: 'mm',
		color: '#0dcaf0',
		category: 'precipitation',
	},
	rh: {
		name: 'Relative Humidity',
		unit: '%',
		color: '#20c997',
		category: 'sensor',
	},
	ap: {
		name: 'Atmospheric Pressure',
		unit: 'kPa',
		color: '#6f42c1',
		category: 'sensor',
	},
	ws: {
		name: 'Wind Speed',
		unit: 'm/s',
		color: '#198754',
		category: 'sensor',
	},
	wd: {
		name: 'Wind Direction',
		unit: '°',
		color: '#0d6efd',
		category: 'sensor',
	},
	wg: {
		name: 'Wind Gusts',
		unit: 'm/s',
		color: '#dc3545',
		category: 'sensor',
	},
	ra: {
		name: 'Solar Radiation',
		unit: 'W/m²',
		color: '#ffc107',
		category: 'sensor',
	},

	// Battery and logger variables
	lb: {
		name: 'Battery Level',
		unit: '%',
		color: '#dc3545',
		category: 'battery',
	},
	lv: {
		name: 'Battery Voltage',
		unit: 'V',
		color: '#0d6efd',
		category: 'battery',
	},
	lp: {
		name: 'Logger Reference Pressure',
		unit: 'kPa',
		color: '#6610f2',
		category: 'logger',
	},
	lt: {
		name: 'Logger Temperature',
		unit: '°C',
		color: '#fd7e14',
		category: 'logger',
	},

	// Precipitation variants
	cp: {
		name: 'Cumulative Precipitation',
		unit: 'mm',
		color: '#17a2b8',
		category: 'precipitation',
	},
	pd: {
		name: 'Precipitation Drop Count',
		unit: '-',
		color: '#0dcaf0',
		category: 'precipitation',
	},
	pt: {
		name: 'Precipitation Tip Count',
		unit: '-',
		color: '#0dcaf0',
		category: 'precipitation',
	},

	// Lightning variables
	ld: {
		name: 'Lightning Distance',
		unit: 'km',
		color: '#e83e8c',
		category: 'lightning',
	},
	le: {
		name: 'Lightning Events',
		unit: '-',
		color: '#e83e8c',
		category: 'lightning',
	},

	// Tilt and orientation
	tx: {
		name: 'Tilt X-Axis',
		unit: '°',
		color: '#fd7e14',
		category: 'orientation',
	},
	ty: {
		name: 'Tilt Y-Axis',
		unit: '°',
		color: '#20c997',
		category: 'orientation',
	},
	ta: {
		name: 'Tilt Angle',
		unit: '°',
		color: '#6f42c1',
		category: 'orientation',
	},

	// Water-related variables
	dw: {
		name: 'Water Depth',
		unit: 'mm',
		color: '#17a2b8',
		category: 'water',
	},
	wl: {
		name: 'Water Level',
		unit: 'm',
		color: '#17a2b8',
		category: 'water',
	},
	tw: {
		name: 'Water Temperature',
		unit: '°C',
		color: '#fd7e14',
		category: 'water',
	},
	wv: {
		name: 'Water Velocity',
		unit: 'm/s',
		color: '#198754',
		category: 'water',
	},
	wq: {
		name: 'Water Discharge',
		unit: 'm³/s',
		color: '#17a2b8',
		category: 'water',
	},

	// Electrical conductivity
	ec: {
		name: 'Electrical Conductivity (Precipitation)',
		unit: 'mS/cm',
		color: '#ffc107',
		category: 'electrical',
	},
	ew: {
		name: 'Water Electrical Conductivity',
		unit: 'mS/cm',
		color: '#ffc107',
		category: 'electrical',
	},
	se: {
		name: 'Soil Electrical Conductivity',
		unit: 'mS/cm',
		color: '#ffc107',
		category: 'electrical',
	},

	// Soil variables
	sm: {
		name: 'Soil Moisture',
		unit: 'm³/m³',
		color: '#8B4513',
		category: 'soil',
	},
	st: {
		name: 'Soil Temperature',
		unit: '°C',
		color: '#fd7e14',
		category: 'soil',
	},
	mp: {
		name: 'Matric Potential',
		unit: 'kPa',
		color: '#6f42c1',
		category: 'soil',
	},

	// Additional sensors
	vp: {
		name: 'Vapor Pressure',
		unit: 'kPa',
		color: '#20c997',
		category: 'sensor',
	},
	ht: {
		name: 'Humidity Sensor Temperature',
		unit: '°C',
		color: '#fd7e14',
		category: 'sensor',
	},
} as const;

// Annotation options - updated to include more variables
const ANNOTATION_OPTIONS = [
	{ key: 'dripClog', label: 'Drip Clog Periods', icon: 'fa-tint', color: 'danger', variables: ['pr', 'cp', 'pd', 'pt'], apiType: 'drip_clog_periods' },
	{ key: 'rainfallBadLightning', label: 'Rainfall Bad Lightning', icon: 'fa-bolt', color: 'warning', variables: ['pr', 'cp', 'pd', 'pt'], apiType: 'rainfall_bad_lightning_periods' },
	{ key: 'severeBatteryIssues', label: 'Severe Battery Issue', icon: 'fa-battery-quarter', color: 'danger', variables: ['lb'], apiType: 'severe_battery_issue' },
	{ key: 'offline', label: 'Offline', icon: 'fa-plug', color: 'secondary', variables: ['lb'], apiType: 'offline' },
	{ key: 'rapidBatteryDrop', label: 'Rapid Fall', icon: 'fa-arrow-down', color: 'warning', variables: ['lb'], apiType: 'rapid_fall' },
	{ key: 'lowCapacity', label: 'Low Battery Capacity', icon: 'fa-battery-empty', color: 'warning', variables: ['lb'], apiType: 'low_capacity' },
	// { key: 'badSatellitePeriods', label: 'Bad Satellite Data', icon: 'fa-satellite-dish', color: 'secondary', variables: ['pr', 'cp', 'pd', 'pt'], apiType: 'rainfall_bad_satellite_periods' },
	// { key: 'stormyNoRain', label: 'Stormy No Rain', icon: 'fa-cloud-bolt', color: 'info', variables: ['pr', 'cp', 'pd', 'pt'], apiType: 'stormy_no_rain' },
	{ key: 'humidityQcSevere', label: 'QC: Humidity Severe', icon: 'fa-flag', color: 'warning', variables: ['rh'], apiType: 'humidity_quality_tahmo_severe' },
	{ key: 'solarRadiationQcSevere', label: 'QC: Solar Radiation Severe', icon: 'fa-flag', color: 'warning', variables: ['ra'], apiType: 'solar_radiation_quality_tahmo_severe' },
	{ key: 'qcFlags', label: 'QC Flags', icon: 'fa-flag', color: 'warning', variables: Object.keys(VARIABLE_CONFIG) },
	{ key: 'issueAnnotations', label: 'Issues & Comments', icon: 'fa-exclamation-triangle', color: 'danger', variables: Object.keys(VARIABLE_CONFIG) },
	{ key: 'dataGaps', label: 'Data Gaps', icon: 'fa-pause', color: 'secondary', variables: Object.keys(VARIABLE_CONFIG) },
	{ key: 'maintenancePeriods', label: 'Maintenance Reports', icon: 'fa-wrench', color: 'info', variables: Object.keys(VARIABLE_CONFIG) },
	{ key: 'odkSubmissions', label: 'Host Submissions', icon: 'fa-file-text', color: 'info', variables: Object.keys(VARIABLE_CONFIG) },
];

// Data filter options
const DATA_FILTER_OPTIONS = [
	{ key: 'raw', label: 'Raw Data', description: 'Show all unprocessed data points' },
	// { key: 'hourly', label: 'Hourly Average', description: 'Aggregate data by hour' },
	{ key: 'daily', label: 'Daily Average', description: 'Aggregate data by day' },
	// { key: 'outliers', label: 'Remove Outliers', description: 'Filter out statistical outliers' },
	// { key: 'interpolated', label: 'Fill Gaps', description: 'Interpolate missing data points' }
];

// Create a mapping of annotation keys to their visual configurations
const ANNOTATION_CONFIGS: Record<
	string,
	{
		type: string;
		label: string;
		color: string;
		backgroundColor: string;
		borderStyle?: number[];
		labelPosition?: 'top' | 'center' | 'bottom';
	}
> = {
	dripClog: {
		type: 'drip_clog_periods',
		label: 'Drip Clog',
		color: '#ff0000',
		backgroundColor: 'rgba(255, 0, 0, 0.3)',
		borderStyle: [5, 5],
		labelPosition: 'top',
	},
	rainfallBadLightning: {
		type: 'rainfall_bad_lightning_periods',
		label: 'Bad Lightning',
		color: '#f0ad4e',
		backgroundColor: 'rgba(240, 173, 78, 0.25)',
		borderStyle: [6, 4],
		labelPosition: 'center',
	},
	severeBatteryIssues: {
		type: 'severe_battery_issue',
		label: 'Severe Battery',
		color: '#dc3545',
		backgroundColor: 'rgba(220, 53, 69, 0.25)',
		borderStyle: [10, 5],
		labelPosition: 'top',
	},
	offline: {
		type: 'offline',
		label: 'Offline',
		color: '#6c757d',
		backgroundColor: 'rgba(108, 117, 125, 0.25)',
		borderStyle: [4, 4],
		labelPosition: 'center',
	},
	rapidBatteryDrop: {
		type: 'rapid_fall',
		label: 'Rapid Fall',
		color: '#ffc107',
		backgroundColor: 'rgba(255, 193, 7, 0.25)',
		borderStyle: [8, 4],
		labelPosition: 'center',
	},
	lowCapacity: {
		type: 'low_capacity',
		label: 'Low Capacity',
		color: '#fd7e14',
		backgroundColor: 'rgba(253, 126, 20, 0.25)',
		borderStyle: [6, 3],
		labelPosition: 'center',
	},
	badSatellitePeriods: {
		type: 'rainfall_bad_satellite_periods',
		label: 'Bad Satellite',
		color: '#6c757d',
		backgroundColor: 'rgba(108, 117, 125, 0.25)',
		borderStyle: [5, 5],
		labelPosition: 'center',
	},
	stormyNoRain: {
		type: 'stormy_no_rain',
		label: 'Stormy No Rain',
		color: '#17a2b8',
		backgroundColor: 'rgba(23, 162, 184, 0.25)',
		borderStyle: [7, 3],
		labelPosition: 'bottom',
	},
	humidityQcSevere: {
		type: 'humidity_quality_tahmo_severe',
		label: 'QC: Humidity',
		color: '#e83e8c',
		backgroundColor: 'rgba(232, 62, 140, 0.25)',
		borderStyle: [4, 4],
		labelPosition: 'top',
	},
	solarRadiationQcSevere: {
		type: 'solar_radiation_quality_tahmo_severe',
		label: 'QC: Solar Rad',
		color: '#6610f2',
		backgroundColor: 'rgba(102, 16, 242, 0.25)',
		borderStyle: [4, 4],
		labelPosition: 'top',
	},
};

// Date preset options
const DATE_RANGE_PRESETS = [
	{ key: 'today', label: 'Today', icon: 'fa-calendar-day' },
	{ key: 'yesterday', label: 'Yesterday', icon: 'fa-calendar-minus' },
	{ key: 'last3days', label: 'Last 3 Days', icon: 'fa-calendar-week' },
	{ key: 'lastweek', label: 'Last Week', icon: 'fa-calendar-week' },
	{ key: 'lastmonth', label: 'Last Month', icon: 'fa-calendar-month' },
	{ key: 'last3months', label: 'Last 3 Months', icon: 'fa-calendar-alt' },
	{ key: 'thismonth', label: 'This Month', icon: 'fa-calendar-check' },
	{ key: 'thisyear', label: 'This Year', icon: 'fa-calendar-check' },
];

type VariableType = keyof typeof VARIABLE_CONFIG;
type AggregationType = 'raw' | 'daily';

const getRecommendedAggregation = (start: Date, end: Date): AggregationType => {
	const rangeDays = Math.max(1, moment(end).diff(moment(start), 'days'));
	if (rangeDays > 31) return 'daily';
	return 'raw';
};

interface Station {
	id: string;
	stationId: string;
	name: string;
	location?: {
		latitude: number;
		longitude: number;
	};
	countryCode: string;
	active: boolean;
}

const VariableTimeSeries: React.FC = () => {
	const { stationCode, variable } = useParams<{ stationCode: string; variable: string }>();
	const navigate = useNavigate();

	// Validate variable parameter
	const validVariable = variable as VariableType;
	const variableConfig = validVariable ? VARIABLE_CONFIG[validVariable] : null;

	// State management
	const [draftDateRange, setDraftDateRange] = useState(() => ({
		start: moment().subtract(1, 'month').toDate(),
		end: moment().toDate(),
	}));
	const [appliedDateRange, setAppliedDateRange] = useState(() => ({
		start: moment().subtract(1, 'month').toDate(),
		end: moment().toDate(),
	}));
	const [isApplyingDateRange, setIsApplyingDateRange] = useState(false);
	const [chartBusy, setChartBusy] = useState(false);
	const [applyLoadStarted, setApplyLoadStarted] = useState(false);

	const [isSliderOpen, setIsSliderOpen] = useState(false);
	// Pre-populate comparison variables for certain primary variables.
	// e.g. battery (lb) -> automatically overlay Solar Radiation (ra) and Battery Voltage (lv)
	const DEFAULT_COMPARISONS: Partial<Record<VariableType, VariableType[]>> = {
		lb: ['ra', 'lv'],
		pr: ['rh', 'le'],
	};
	const [comparisonVariables, setComparisonVariables] = useState<VariableType[]>(
		() => (validVariable ? (DEFAULT_COMPARISONS[validVariable] ?? []) : [])
	);
	const [aggregationType, setAggregationType] = useState<AggregationType>('raw');

	// Keep overlays visible by default for the current variable.
	const [annotations, setAnnotations] = useState<Record<string, boolean>>(() => {
		const initial: Record<string, boolean> = {};
		ANNOTATION_OPTIONS.filter((opt) => validVariable && opt.variables.includes(validVariable)).forEach((opt) => {
			initial[opt.key] = true;
		});
		return initial;
	});

	const [dataFilters, setDataFilters] = useState<Record<string, boolean>>({ raw: true, daily: false });
	const [selectedColocatedStations, setSelectedColocatedStations] = useState<string[]>([]);

	// Zoom sync toggle state
	const [syncLinked, setSyncLinked] = useState<boolean>(false);

	// Station data query
	const {
		data: station,
		isLoading: stationLoading,
		isError: stationError,
	} = useQuery({
		queryKey: ['station', stationCode],
		queryFn: () => getStation(stationCode!).then((res) => res.data as Station),
		enabled: !!stationCode,
		staleTime: 5 * 60 * 1000,
	});

	// Colocated stations query
	const {
		data: colocatedStations,
		isLoading: colocatedLoading,
	} = useQuery({
		queryKey: ['colocatedStations', stationCode],
		queryFn: () => getColocatedStations(stationCode!),
		enabled: !!stationCode,
		staleTime: 10 * 60 * 1000,
	});

	// Pre-select all colocated stations as soon as they load (only on first load)
	const colocatedInitialisedRef = React.useRef(false);
	useEffect(() => {
		if (colocatedInitialisedRef.current) return;
		if (colocatedStations && colocatedStations.length > 0) {
			colocatedInitialisedRef.current = true;
			setSelectedColocatedStations(colocatedStations.map((s: any) => s.id));
		}
	}, [colocatedStations]);

	// Prefetch the current variable for the current date range as soon as stationCode is known.
	// This fires immediately on mount so data arrives before the chart even renders.
	const prefetchInitialisedRef = React.useRef(false);
	useEffect(() => {
		if (prefetchInitialisedRef.current) return;
		if (!stationCode || !validVariable) return;
		prefetchInitialisedRef.current = true;
		const start = moment(appliedDateRange.start).toISOString();
		const end = moment(appliedDateRange.end).toISOString();
		// Prefetch the primary variable
		prefetchTimeSeriesData(stationCode, validVariable, start, end, 'auto');
		// Also prefetch a couple of common companion variables so switching is instant
		const companions: string[] = ['lb', 'te', 'pr', 'ra'].filter((v) => v !== validVariable).slice(0, 2);
		companions.forEach((v) => prefetchTimeSeriesData(stationCode, v, start, end, 'auto'));
	}, [stationCode, validVariable, appliedDateRange.start, appliedDateRange.end]); // eslint-disable-line react-hooks/exhaustive-deps

	// Memoize the colocated stations prop so SimpleTimeSeriesChart doesn't re-render
	// when the parent re-renders for unrelated reasons (the inline .map().filter() was
	// creating a new array reference on every render, defeating React.memo).
	const COLOC_COLORS = ['#17a2b8', '#28a745', '#6610f2', '#fd7e14', '#e83e8c', '#20c997', '#ffc107'];
	const colocatedStationsProp = useMemo(
		() =>
			selectedColocatedStations
				.map((stationId, index) => {
					const colocStation = colocatedStations?.find((s: any) => s.id === stationId);
					if (!colocStation) return null;
					return {
						stationId: colocStation.id,
						liveStationId: colocStation.stationId,
						name: colocStation.name,
						color: COLOC_COLORS[index % COLOC_COLORS.length],
					};
				})
				.filter((s): s is NonNullable<typeof s> => s !== null),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedColocatedStations, colocatedStations]
	);

	// Filter annotations by current variable
	const availableAnnotations = useMemo(() => ANNOTATION_OPTIONS.filter((option) => option.variables.includes(validVariable!)), [validVariable]);

	// When navigating to a different variable, keep overlays enabled by default.
	useEffect(() => {
		const initial: Record<string, boolean> = {};
		availableAnnotations.forEach((opt) => { initial[opt.key] = true; });
		setAnnotations(initial);
	}, [validVariable]); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle date range changes
	const handleStartDateChange = (date: Date | null) => {
		if (date) {
			setDraftDateRange((prev) => ({ ...prev, start: date }));
		}
	};

	const handleEndDateChange = (date: Date | null) => {
		if (date) {
			setDraftDateRange((prev) => ({ ...prev, end: date }));
		}
	};

	// Apply date range preset
	const applyDateRangePreset = (presetKey: string) => {
		let newStart: Date;
		let newEnd: Date;

		const now = moment();

		switch (presetKey) {
			case 'today':
				newStart = now.startOf('day').toDate();
				newEnd = now.endOf('day').toDate();
				break;
			case 'yesterday':
				newStart = now.subtract(1, 'day').startOf('day').toDate();
				newEnd = now.endOf('day').toDate();
				break;
			case 'last3days':
				newStart = now.subtract(3, 'days').startOf('day').toDate();
				newEnd = moment().toDate();
				break;
			case 'lastweek':
				newStart = now.subtract(1, 'week').startOf('day').toDate();
				newEnd = moment().toDate();
				break;
			case 'lastmonth':
				newStart = now.subtract(1, 'month').startOf('day').toDate();
				newEnd = moment().toDate();
				break;
			case 'last3months':
				newStart = now.subtract(3, 'months').startOf('day').toDate();
				newEnd = moment().toDate();
				break;
			case 'thismonth':
				newStart = moment().startOf('month').toDate();
				newEnd = moment().endOf('month').toDate();
				break;
			case 'thisyear':
				newStart = moment().startOf('year').toDate();
				newEnd = moment().endOf('year').toDate();
				break;
			default:
				return;
		}

		setDraftDateRange({
			start: newStart,
			end: newEnd,
		});
	};

	const hasPendingDateRangeChanges = draftDateRange.start.getTime() !== appliedDateRange.start.getTime() || draftDateRange.end.getTime() !== appliedDateRange.end.getTime();

	const applySelectedDateRange = () => {
		if (draftDateRange.start > draftDateRange.end) return;
		if (!hasPendingDateRangeChanges) return;
		setIsApplyingDateRange(true);
		setApplyLoadStarted(false);
		setAppliedDateRange({
			start: draftDateRange.start,
			end: draftDateRange.end,
		});
	};

	const handleChartBusyStateChange = (busy: boolean) => {
		setChartBusy(busy);
		if (isApplyingDateRange && busy) {
			setApplyLoadStarted(true);
		}
	};

	// Add comparison variable
	const addComparisonVariable = (variableKey: VariableType) => {
		if (!comparisonVariables.includes(variableKey)) {
			setComparisonVariables((prev) => [...prev, variableKey]);
		}
	};

	// Remove comparison variable
	const removeComparisonVariable = (variableKey: VariableType) => {
		setComparisonVariables((prev) => prev.filter((v) => v !== variableKey));
	};

	// Toggle colocated station
	const toggleColocatedStation = (stationId: string) => {
		setSelectedColocatedStations((prev) => {
			if (prev.includes(stationId)) {
				return prev.filter((id) => id !== stationId);
			} else {
				return [...prev, stationId];
			}
		});
	};

	// Toggle annotation
	const toggleAnnotation = (key: string) => {
		setAnnotations((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	// Toggle data filter
	const toggleDataFilter = (key: string) => {
		setDataFilters((prev) => {
			const newFilters = { ...prev }; // Toggle the clicked filter

			const newValue = !prev[key];

				if (key === 'raw') {
					if (newValue) {
						// Switching TO raw
						newFilters.raw = true;
						newFilters.daily = false;
						setAggregationType('raw');
					}
				} else if (key === 'daily') {
					if (newValue) {
						// Switching TO daily
						newFilters.raw = false;
						newFilters.daily = true;
						setAggregationType('daily');
					} else {
						// Switching OFF daily - default back to raw
						newFilters.raw = true;
						newFilters.daily = false;
						setAggregationType('raw');
					}
			}

			return newFilters;
		});
	};

	// Format dates for API
	const formattedDates = useMemo(
		() => ({
			start: moment(appliedDateRange.start).format('YYYY-MM-DDTHH:mm:ss'),
			end: moment(appliedDateRange.end).format('YYYY-MM-DDTHH:mm:ss'),
		}),
		[appliedDateRange]
	);

	// Keep default aggregation adaptive to selected date range.
	useEffect(() => {
		const recommended = getRecommendedAggregation(appliedDateRange.start, appliedDateRange.end);
		setAggregationType(recommended);
		setDataFilters({
			raw: recommended === 'raw',
			daily: recommended === 'daily',
		});
	}, [appliedDateRange.start, appliedDateRange.end]);

	useEffect(() => {
		if (!isApplyingDateRange) return;
		if (!applyLoadStarted) return;
		if (chartBusy) return;
		setIsApplyingDateRange(false);
		setApplyLoadStarted(false);
	}, [isApplyingDateRange, applyLoadStarted, chartBusy]);

	// Sensors metadata query - fetch to get multiple sensor information
	const {
		data: sensorsMetadata,
	} = useQuery({
		queryKey: ['sensorsMetadata', stationCode, validVariable, formattedDates.start, formattedDates.end],
		queryFn: () => getTimeSeriesData(
			stationCode!,
			validVariable!,
			formattedDates.start.split('T')[0],
			formattedDates.end.split('T')[0],
			getRecommendedAggregation(appliedDateRange.start, appliedDateRange.end)
		).then((res) => res.data),
		enabled: !!stationCode && !!validVariable,
		staleTime: 5 * 60 * 1000,
		select: (data) => data?.metadata?.additional_sensors || [],
	});

	// Get available variables for comparison (excluding current variable and already selected ones)
	const availableComparisonOptions = useMemo(
		() => Object.entries(VARIABLE_CONFIG).filter(([key]) => key !== validVariable && !comparisonVariables.includes(key as VariableType)),
		[validVariable, comparisonVariables]
	);

	// Group variables by category for better organization in dropdown
	const groupedComparisonOptions = useMemo(() => {
		const groups: Record<string, Array<[string, (typeof VARIABLE_CONFIG)[keyof typeof VARIABLE_CONFIG]]>> = {};

		availableComparisonOptions.forEach(([key, config]) => {
			if (!groups[config.category]) {
				groups[config.category] = [];
			}
			groups[config.category].push([key, config]);
		});

		return groups;
	}, [availableComparisonOptions]);

	// Compute a stable key from only the non-excluded annotation entries so that
	// toggling issueAnnotations/qcFlags etc. doesn't create a new array reference
	// (which would re-run the overlay fetch + chartKey effect unnecessarily).
	const EXCLUDED_ANNOTATION_KEYS = ['qcFlags', 'issueAnnotations', 'dataGaps', 'maintenancePeriods', 'odkSubmissions'];
	const activeAnnotationKey = Object.entries(annotations)
		.filter(([k, v]) => v && !EXCLUDED_ANNOTATION_KEYS.includes(k))
		.map(([k]) => k)
		.sort()
		.join(',');
	const activeAnnotationConfigs = useMemo(() => {
		const configs = [];
		for (const [key, isEnabled] of Object.entries(annotations)) {
			if (!isEnabled) continue;
			if (EXCLUDED_ANNOTATION_KEYS.includes(key)) continue;
			const config = ANNOTATION_CONFIGS[key];
			if (config) configs.push(config);
		}
		return configs;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeAnnotationKey]);

	// Error states
	if (!stationCode) {
		return (
			<div className='content-page'>
				<div className='content'>
					<div className='container-fluid'>
						<div className='alert alert-danger'>
							<i className='fa fa-exclamation-triangle me-2'></i>
							Station code is required
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (!validVariable || !variableConfig) {
		return (
			<div className='content-page'>
				<div className='content'>
					<div className='container-fluid'>
						<div className='alert alert-danger'>
							<i className='fa fa-exclamation-triangle me-2'></i>
							Invalid variable: {variable}. Valid variables are: {Object.keys(VARIABLE_CONFIG).join(', ')}
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (stationError) {
		return (
			<div className='content-page'>
				<div className='content'>
					<div className='container-fluid'>
						<div className='alert alert-danger'>
							<i className='fa fa-exclamation-triangle me-2'></i>
							Failed to load station data
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='content-page'>
			<div className='content'>
				<div className='container-fluid' style={{ position: 'relative' }}>
					{/* Header */}
					<div className='row'>
						<div className='col-12'>
							<div className='page-title-box'>
								<div className='page-title-right d-flex align-items-center'>
									<ol className='breadcrumb m-0 me-3'>
										<li className='breadcrumb-item'>
											<Link to='/'>Home</Link>
										</li>
										<li className='breadcrumb-item active'>{variableConfig.name}</li>
									</ol>
									{/* Slider Toggle Button */}
									<button className={`btn btn-${isSliderOpen ? 'secondary' : 'primary'} btn-sm`} onClick={() => setIsSliderOpen(!isSliderOpen)} title='Chart Options'>
										<i className={`fa fa-${isSliderOpen ? 'times' : 'sliders'} me-1`}></i>
										Options
									</button>
									{/* Zoom Sync Toggle */}
									<button
										className={`btn btn-sm ms-2 ${syncLinked ? 'btn-success' : 'btn-outline-success'}`}
										onClick={() => setSyncLinked(!syncLinked)}
										title={syncLinked ? 'Unlink zoom between charts' : 'Link zoom between charts'}>
										<i className={`fa ${syncLinked ? 'fa-link' : 'fa-unlink'} me-1`}></i>
										{syncLinked ? 'Linked' : 'Link Zoom'}
									</button>
								</div>
								<h4 className='page-title'>
									<i className='fa fa-line-chart me-2' style={{ color: variableConfig.color }}></i>
									{variableConfig.name} Time Series
								</h4>
								{station && (
									<div className='d-flex align-items-center justify-content-between'>
										<p className='text-muted mb-0'>
											Station: <strong>{station.name}</strong> ({station.id})
											<span className={`badge ms-2 ${station.active ? 'bg-success' : 'bg-secondary'}`}>{station.active ? 'Active' : 'Inactive'}</span>
										</p>
										<p className='text-muted mb-0'>
											<i className='fa fa-calendar me-1'></i>
											{moment(appliedDateRange.start).format('MMM DD, YYYY')} - {moment(appliedDateRange.end).format('MMM DD, YYYY')}
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Main Chart */}
					<div className='row'>
						<div className='col-12'>
							<div style={{ position: 'relative' }}>
								<SimpleTimeSeriesChart
									stationId={stationCode}
									liveStationId={station?.stationId}
									variable={validVariable}
									startDate={formattedDates.start}
									endDate={formattedDates.end}
									height={500}
									title={`${variableConfig.name} (${variableConfig.unit})`}
									yAxisLabel={`${variableConfig.name} (${variableConfig.unit})`}
									aggregation={aggregationType}
									chartType={validVariable === 'pr' ? 'bar' : 'line'}
									color={variableConfig.color}
									showDataInfo={true}
									enableZoom={true}
									showDataGaps={annotations.dataGaps || false}
									showQCFlags={annotations.qcFlags || false}
									showIssueAnnotations={annotations.issueAnnotations || false}
									showMaintenanceRecordsAnnotations={annotations.maintenancePeriods || false}
									showODKSubmissions={annotations.odkSubmissions || false}
									annotationConfigs={activeAnnotationConfigs}
									syncEnabled={syncLinked}
									syncKey={`station-${stationCode}`}
									comparisonVariables={comparisonVariables}
									colocatedStations={colocatedStationsProp}
									onBusyStateChange={handleChartBusyStateChange}
								/>
								{isApplyingDateRange && (
									<div
										style={{
											position: 'absolute',
											inset: 0,
											background: 'rgba(255, 255, 255, 0.96)',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexDirection: 'column',
											zIndex: 20,
										}}>
										<div className='spinner-border text-success mb-2' role='status' aria-hidden='true'></div>
										<div className='fw-semibold text-success'>Applying Date Range...</div>
										<small className='text-muted'>Loading chart data for selected dates</small>
									</div>
								)}
							</div>
						</div>
						</div>


					{/* Right Sidebar Slider */}
					<div
						className={`position-fixed top-0 end-0 h-100 bg-white shadow-lg border-start transition-all ${isSliderOpen ? 'translate-x-0' : 'translate-x-100'}`}
						style={{
							width: '420px',
							zIndex: 1050,
							transform: isSliderOpen ? 'translateX(0)' : 'translateX(100%)',
							transition: 'transform 0.3s ease-in-out',
							overflowY: 'auto',
						}}>
						<div className='p-4'>
							{/* Header */}
							<div className='d-flex justify-content-between align-items-center mb-4'>
								<h5 className='mb-0'>
									<i className='fa fa-cog me-2'></i>
									Chart Options
								</h5>
								<button className='btn btn-sm btn-outline-secondary' onClick={() => setIsSliderOpen(false)}>
									<i className='fa fa-times'></i>
								</button>
							</div>

							{/* Date Range Section */}
							<div className='mb-4'>
								<h6 className='border-bottom pb-2 mb-3'>
									<i className='fa fa-calendar me-2 text-success'></i>
									Date Range
								</h6>

								{/* Current Date Range Display */}
								<div className='alert alert-light border mb-3'>
									<div className='d-flex justify-content-between align-items-center'>
										<div>
											<small className='text-muted'>Current Range:</small>
											<div className='fw-semibold'>
												{moment(appliedDateRange.start).format('MMM DD, YYYY')} - {moment(appliedDateRange.end).format('MMM DD, YYYY')}
											</div>
											<small className='text-muted'>{moment(appliedDateRange.end).diff(moment(appliedDateRange.start), 'days') + 1} days</small>
											{hasPendingDateRangeChanges && (
												<div className='text-warning mt-1'>
													<i className='fa fa-clock-o me-1'></i>
													Pending changes not applied
												</div>
											)}
										</div>
										<i className='fa fa-calendar-o text-success fa-lg'></i>
									</div>
								</div>

								{/* Quick Range Buttons */}
								<div className='mb-3'>
									<label className='form-label small fw-semibold'>Quick Select:</label>
									<div className='row g-2'>
										{DATE_RANGE_PRESETS.map((preset) => (
											<div key={preset.key} className='col-6'>
												<button
													type='button'
													className='btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center'
													onClick={() => applyDateRangePreset(preset.key)}
													title={preset.label}>
													<i className={`fa ${preset.icon} me-2`}></i>
													<span className='text-truncate'>{preset.label}</span>
												</button>
											</div>
										))}
									</div>
								</div>

								{/* Manual Date Pickers */}
								<div className='mb-3'>
									<label className='form-label small fw-semibold'>Custom Range:</label>
									<div className='row g-2'>
										<div className='col-6'>
											<label className='form-label small text-muted'>From</label>
											<DatePicker
												selected={draftDateRange.start}
												onChange={handleStartDateChange}
												selectsStart
												startDate={draftDateRange.start}
												endDate={draftDateRange.end}
												maxDate={draftDateRange.end}
												className='form-control form-control-sm'
												dateFormat='yyyy-MM-dd'
												placeholderText='Start date'
											/>
										</div>
										<div className='col-6'>
											<label className='form-label small text-muted'>To</label>
											<DatePicker
												selected={draftDateRange.end}
												onChange={handleEndDateChange}
												selectsEnd
												startDate={draftDateRange.start}
												endDate={draftDateRange.end}
												minDate={draftDateRange.start}
												maxDate={new Date()}
												className='form-control form-control-sm'
												dateFormat='yyyy-MM-dd'
												placeholderText='End date'
											/>
										</div>
									</div>
								</div>

								<div className='d-grid mb-2'>
									<button
										type='button'
										className='btn btn-success btn-sm'
										onClick={applySelectedDateRange}
										disabled={!hasPendingDateRangeChanges || draftDateRange.start > draftDateRange.end || isApplyingDateRange}>
										{isApplyingDateRange ? (
											<>
												<span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
												Applying Date Range...
											</>
										) : (
											<>
												<i className='fa fa-check me-2'></i>
												Apply Date Range
											</>
										)}
									</button>
								</div>

								{/* Reset to Default Button */}
								<div className='d-grid'>
									<button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => applyDateRangePreset('lastweek')}>
										<i className='fa fa-refresh me-2'></i>
										Reset to Last Week
									</button>
								</div>
							</div>

							{/* Comparison Section */}
							<div className='mb-4'>
								<h6 className='border-bottom pb-2 mb-3'>
									<i className='fa fa-plus-circle me-2 text-primary'></i>
									Add Variable to Chart
								</h6>

								{/* Selected Variables */}
								{comparisonVariables.length > 0 && (
									<div className='mb-3'>
										<label className='form-label small'>Overlaid Variables:</label>
										<div className='d-flex flex-wrap gap-2'>
											{comparisonVariables.map((varKey) => (
												<span key={varKey} className='badge bg-primary d-flex align-items-center' style={{ fontSize: '12px' }}>
													{VARIABLE_CONFIG[varKey].name}
													<button
														type='button'
														className='btn-close btn-close-white ms-2'
														aria-label='Remove'
														style={{ fontSize: '8px' }}
														onClick={() => removeComparisonVariable(varKey)}></button>
												</span>
											))}
										</div>
									</div>
								)}

								{/* Add Variable Dropdown with Grouped Options */}
								{Object.keys(groupedComparisonOptions).length > 0 ? (
									<div className='form-group'>
										<label className='form-label small'>Add variable to chart:</label>
										<select
											className='form-select form-select-sm'
											value=''
											onChange={(e) => {
												if (e.target.value) {
													addComparisonVariable(e.target.value as VariableType);
													e.target.value = ''; // Reset dropdown
												}
											}}>
											<option value=''>Select a variable...</option>
											{Object.entries(groupedComparisonOptions).map(([category, variables]) => (
												<optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
													{variables.map(([key, config]) => (
														<option key={key} value={key}>
															{config.name} ({config.unit})
														</option>
													))}
												</optgroup>
											))}
										</select>
									</div>
								) : (
									<p className='text-muted small'>{comparisonVariables.length === 0 ? 'All variables are already selected' : 'No more variables available'}</p>
								)}

								{comparisonVariables.length > 0 && (
									<div className='mt-2'>
										<small className='text-success'>
											<i className='fa fa-check me-1'></i>
											{comparisonVariables.length} variable{comparisonVariables.length !== 1 ? 's' : ''} overlaid on same chart
										</small>
									</div>
								)}
							</div>

							{/* Multiple Sensors Section */}
							{sensorsMetadata && sensorsMetadata.length > 0 && (
								<div className='mb-4'>
									<h6 className='border-bottom pb-2 mb-3'>
										<i className='fa fa-layer-group me-2 text-info'></i>
										Multiple Sensors
									</h6>
									<p className='text-muted small mb-2'>
										<i className='fa fa-info-circle me-1'></i>
										This variable has {sensorsMetadata.length} sensor{sensorsMetadata.length !== 1 ? 's' : ''}
									</p>
									{sensorsMetadata.map((sensor: any) => (
										<div key={sensor.sensor} className='mb-2 ps-2'>
											<i className='fa fa-circle me-2 text-info' style={{ fontSize: '8px' }}></i>
											<span className='small'>{sensor.name}</span>
										</div>
									))}
								</div>
							)}

							{/* Colocated Stations Section */}
							<div className='mb-4'>
								<h6 className='border-bottom pb-2 mb-3'>
									<i className='fa fa-map-marker me-2 text-success'></i>
									Colocated Stations
								</h6>
								{colocatedLoading ? (
									<div className='d-flex flex-column align-items-center justify-content-center py-3'>
										<div className='spinner-border spinner-border-sm text-success' role='status'>
											<span className='visually-hidden'>Loading colocated stations...</span>
										</div>
										<div className='mt-2 text-muted small'>Loading colocated stations...</div>
									</div>
								) : colocatedStations && colocatedStations.length > 0 ? (
									<>
										<p className='text-muted small mb-2'>
											<i className='fa fa-info-circle me-1'></i>
											Stations at the same location ({colocatedStations.length})
										</p>
										{colocatedStations.map((colocStation: any) => (
											<div key={colocStation.id} className='form-check mb-2'>
												<input
													className='form-check-input'
													type='checkbox'
													id={`coloc-${colocStation.id}`}
													checked={selectedColocatedStations.includes(colocStation.id)}
													onChange={() => toggleColocatedStation(colocStation.id)}
												/>
												<label className='form-check-label d-flex align-items-center justify-content-between' htmlFor={`coloc-${colocStation.id}`}>
													<span>
														<i className={`fa fa-circle me-2 ${colocStation.active ? 'text-success' : 'text-secondary'}`} style={{ fontSize: '8px' }}></i>
														{colocStation.name}
													</span>
													<small className='text-muted ms-2'>({colocStation.id})</small>
												</label>
											</div>
										))}
										{selectedColocatedStations.length > 0 && (
											<div className='mt-2'>
												<small className='text-success'>
													<i className='fa fa-check me-1'></i>
													{selectedColocatedStations.length} station{selectedColocatedStations.length !== 1 ? 's' : ''} selected
												</small>
											</div>
										)}
									</>
								) : (
									<div className='alert alert-light border py-2 px-3'>
										<small className='text-muted mb-0'>
											<i className='fa fa-info-circle me-1'></i>
											No colocated stations found
										</small>
									</div>
								)}
							</div>

							{/* Annotations Section */}
							<div className='mb-4'>
								<h6 className='border-bottom pb-2 mb-3'>
									<i className='fa fa-tags me-2 text-warning'></i>
									Chart Annotations
								</h6>
								{availableAnnotations.map((annotation) => (
									<div key={annotation.key} className='form-check mb-2'>
										<input
											className='form-check-input'
											type='checkbox'
											id={`annotation-${annotation.key}`}
											checked={annotations[annotation.key] || false}
											onChange={() => toggleAnnotation(annotation.key)}
										/>
										<label className='form-check-label d-flex align-items-center' htmlFor={`annotation-${annotation.key}`}>
											<i className={`fa ${annotation.icon} me-2 text-${annotation.color}`}></i>
											{annotation.label}
										</label>
									</div>
								))}
								{availableAnnotations.length === 0 && <p className='text-muted small'>No annotations available for this variable</p>}
							</div>

							{/* Data Filters Section */}
							<div className='mb-4'>
								<h6 className='border-bottom pb-2 mb-3'>
									<i className='fa fa-filter me-2 text-info'></i>
									Data Processing
								</h6>
								{DATA_FILTER_OPTIONS.map((filter) => (
									<div key={filter.key} className='mb-3'>
										<div className='form-check'>
											<input
												className='form-check-input'
												type='radio'
												name='aggregation'
												id={`filter-${filter.key}`}
												checked={dataFilters[filter.key] || false}
													onChange={() => {
														setAggregationType(filter.key as AggregationType);
														setDataFilters({
															raw: filter.key === 'raw',
															daily: filter.key === 'daily',
														});
													}}
											/>
											<label className='form-check-label fw-semibold' htmlFor={`filter-${filter.key}`}>
												{filter.label}
											</label>
										</div>
										<small className='text-muted ms-4'>{filter.description}</small>
									</div>
								))}
							</div>

							{/* Active Settings Summary */}
							<div className='border-top pt-3'>
								<h6 className='mb-2'>Active Settings</h6>
								<div className='d-flex flex-wrap gap-1 mb-2'>
									<span className='badge bg-success'>
										{moment(appliedDateRange.start).format('MMM DD')} - {moment(appliedDateRange.end).format('MMM DD')}
									</span>
								</div>
								<div className='d-flex flex-wrap gap-1'>
									{Object.entries(annotations)
										.filter(([_, active]) => active)
										.map(([key]) => (
											<span key={key} className='badge bg-warning text-dark'>
												{availableAnnotations.find((a) => a.key === key)?.label}
											</span>
										))}
									{Object.entries(dataFilters)
										.filter(([_, active]) => active)
										.map(([key]) => (
											<span key={key} className='badge bg-info'>
												{DATA_FILTER_OPTIONS.find((f) => f.key === key)?.label}
											</span>
										))}
									{comparisonVariables.map((varKey) => (
										<span key={varKey} className='badge bg-primary'>
											+ {VARIABLE_CONFIG[varKey].name}
										</span>
									))}
									{selectedColocatedStations.map((stationId) => {
										const station = colocatedStations?.find((s: any) => s.id === stationId);
										return (
											<span key={stationId} className='badge bg-success'>
												<i className='fa fa-map-marker me-1'></i>
												{station?.name || stationId}
											</span>
										);
									})}
								</div>
								{Object.values(annotations).every((v) => !v) &&
									Object.values(dataFilters).every((v) => !v) &&
									comparisonVariables.length === 0 &&
									selectedColocatedStations.length === 0 && <small className='text-muted'>No active customizations (except date range)</small>}
							</div>
						</div>
					</div>

					{/* Overlay when slider is open */}
					{isSliderOpen && <div className='position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25' style={{ zIndex: 1040 }} onClick={() => setIsSliderOpen(false)} />}
				</div>
			</div>
		</div>
	);
};

export default VariableTimeSeries;
